import React from 'react';
import { Box, Drawer, List, ListItem, ListItemButton, IconButton, useMediaQuery, useTheme, Divider } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const NavigationMenu = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, logout, user } = useAuth();

  // Lead with the core tools; keep trades grouped so it's not one long list.
  // Hide "Home" for signed-in users — they land on /dashboard instead.
  const topItems = isAuthenticated
    ? [{ label: 'My Dashboard', path: '/dashboard' }]
    : [{ label: 'Home', path: '/' }];
  topItems.push(
    { label: 'Simulate Season', path: '/simulate' },
    { label: 'Manager Intel', path: '/intel' },
    { label: 'Marketplaces', path: '/marketplaces' }
  );

  const sections = [
    { label: null, items: topItems },
    {
      label: 'Trades',
      items: [
        { label: 'Trade Packages', path: isAuthenticated ? '/trade-packages/dashboard' : '/trade-packages' },
        { label: 'Rate My Trade', path: '/rate-my-trade' },
      ],
    },
  ];

  if (isAuthenticated && user?.isAdmin) {
    sections.push({ label: null, items: [{ label: 'Admin', path: '/admin' }] });
  }

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    onClose();
  };

  const handleLogin = () => {
    navigate('/login');
    onClose();
  };

  const isCurrentPath = (path) => {
    return location.pathname === path;
  };

  return (
    <Drawer
      anchor={isMobile ? 'right' : 'right'}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMobile ? '80%' : '240px',
          maxWidth: isMobile ? '320px' : '240px',
          background: '#0E0E10',
          color: '#FFFFFF',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: isMobile ? 'none' : '0 12px 32px rgba(0, 0, 0, 0.5)',
          borderRadius: isMobile ? 0 : '12px',
          right: isMobile ? 0 : '16px',
          top: isMobile ? 0 : '72px',
          height: isMobile ? '100%' : 'auto',
          padding: '16px 0',
        },
      }}
      ModalProps={{
        BackdropProps: {
          sx: {
            background: 'rgba(0, 0, 0, 0.6)',
          },
        },
      }}
    >
      {/* Header with close button (mobile only) */}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '8px 16px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            marginBottom: '8px',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              color: '#FFFFFF',
              width: '44px',
              height: '44px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.06)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* User info section (when logged in) */}
      {isAuthenticated && user?.email && (
        <Box
          sx={{
            padding: '16px 16px 12px',
            marginBottom: '8px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <Box
            sx={{
              fontSize: '11px',
              fontWeight: 500,
              letterSpacing: '1px',
              color: 'rgba(255,255,255,0.5)',
              textTransform: 'uppercase',
              marginBottom: '6px',
            }}
          >
            Signed in as
          </Box>
          <Box
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#FFFFFF',
              letterSpacing: '-0.01em',
              wordBreak: 'break-word',
            }}
          >
            {user.email}
          </Box>
        </Box>
      )}

      {/* Menu items */}
      <Box sx={{ padding: '0 8px' }}>
        {sections.map((section, sectionIdx) => (
          <Box
            key={sectionIdx}
            sx={{ marginBottom: sectionIdx < sections.length - 1 ? '12px' : 0 }}
          >
            {section.label && (
              <Box
                sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.5)',
                  padding: '8px 16px 6px',
                }}
              >
                {section.label}
              </Box>
            )}
            <List sx={{ padding: 0 }}>
              {section.items.map((item) => {
                const isCurrent = isCurrentPath(item.path);

                return (
                  <ListItem key={item.path} disablePadding sx={{ marginBottom: '4px' }}>
                    <ListItemButton
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        borderRadius: '8px',
                        padding: '12px 16px',
                        minHeight: '44px',
                        background: isCurrent ? '#FFFFFF' : 'transparent',
                        color: isCurrent ? '#000000' : '#FFFFFF',
                        fontSize: '15px',
                        fontWeight: isCurrent ? 500 : 400,
                        letterSpacing: '-0.01em',
                        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                        '&:hover': {
                          background: isCurrent ? '#FFFFFF' : 'rgba(255, 255, 255, 0.06)',
                          transform: isCurrent ? 'none' : 'translateY(-1px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      {item.label}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Login/Logout section */}
      <Box sx={{ padding: '0 8px', marginTop: '8px' }}>
        <Divider sx={{ marginBottom: '8px', borderColor: 'rgba(255,255,255,0.08)' }} />
        <ListItem disablePadding>
          <ListItemButton
            onClick={isAuthenticated ? handleLogout : handleLogin}
            sx={{
              borderRadius: '8px',
              padding: '12px 16px',
              minHeight: '44px',
              background: 'transparent',
              color: 'rgba(255,255,255,0.65)',
              fontSize: '15px',
              fontWeight: 400,
              letterSpacing: '-0.01em',
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.06)',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            {isAuthenticated ? 'Logout' : 'Login'}
          </ListItemButton>
        </ListItem>
      </Box>
    </Drawer>
  );
};

export default NavigationMenu;
